import { Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import GoogleMap from '../../components/GoogleMap'

// ─── Milestone display config ─────────────────────────────────────────────────
const MILESTONE = {
  queue_activated:    { label: 'Activated',      color: '#2196F3' },
  order_submitted:    { label: 'Order In',        color: '#9C27B0' },
  preparing:          { label: 'Preparing',       color: '#FF9800' },
  ready:              { label: 'Ready',           color: '#4CAF50' },
  guest_eta_update:   { label: 'En Route',        color: '#2196F3' },
  guest_arrived:      { label: 'Arrived',         color: '#FFC107' },
  checkin_verified:   { label: 'Verified',        color: '#009688' },
  delivered:          { label: 'Delivered',       color: '#4CAF50' },
  escrow_released:    { label: 'Released',        color: '#4CAF50' },
  session_closed:     { label: 'Closed',          color: '#607D8B' },
  sensepoint_request: { label: 'Arrival Request', color: '#FF9800' },
  active:             { label: 'Active',          color: '#2196F3' },
  paused:             { label: 'Paused',          color: '#FF5722' },
}

const STATUS_COLORS = {
  active:              { bg: '#E3F2FD', text: '#1565C0', border: '#90CAF9' },
  awaiting_sensepoint: { bg: '#FFF8E1', text: '#F57F17', border: '#FFE082' },
  paused:              { bg: '#FBE9E7', text: '#BF360C', border: '#FFAB91' },
  completed:           { bg: '#E8F5E9', text: '#2E7D32', border: '#A5D6A7' },
  cancelled:           { bg: '#FFEBEE', text: '#B71C1C', border: '#EF9A9A' },
}

function milestoneBadge(key) {
  const m = MILESTONE[key] || { label: key?.replace(/_/g, ' ') || '—', color: '#9E9E9E' }
  return (
    <span style={{ backgroundColor: m.color + '20', color: m.color, border: `1px solid ${m.color}50` }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold">
      <span style={{ backgroundColor: m.color }} className="w-1.5 h-1.5 rounded-full" />
      {m.label}
    </span>
  )
}

function statusBadge(status) {
  const c = STATUS_COLORS[status] || { bg: '#F5F5F5', text: '#616161', border: '#E0E0E0' }
  return (
    <span style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` }}
      className="px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide">
      {status?.replace(/_/g, ' ')}
    </span>
  )
}

function modeBadge(mode) {
  const icons = { cloud: '☁️', edge: '⚡', mobile: '📱' }
  return (
    <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
      {icons[mode] || ''} {mode || 'cloud'}
    </span>
  )
}

function timeAgo(iso) {
  if (!iso) return '—'
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60)  return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  return `${Math.floor(s / 3600)}h ago`
}

// ─── Session card (used in both list view and map panel) ─────────────────────
function SessionCard({ session, compact = false }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${compact ? 'min-w-[280px] max-w-[320px] p-4' : 'p-6'}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="font-bold text-gray-900 truncate">{session.offer_name}</p>
          {session.offer_brand && (
            <p className="text-xs text-gray-500 truncate">{session.offer_brand}</p>
          )}
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-xs text-gray-400 font-mono">#{session.id}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {statusBadge(session.session_status)}
        {milestoneBadge(session.latest_event || session.session_status)}
        {modeBadge(session.sensepoint_mode)}
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-4">
        <div>
          <p className="font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Events</p>
          <p className="text-gray-700 font-mono">{session.event_count}</p>
        </div>
        <div>
          <p className="font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Guest seen</p>
          <p className="text-gray-700">{timeAgo(session.guest_last_seen_at)}</p>
        </div>
        <div>
          <p className="font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Activated</p>
          <p className="text-gray-700">{timeAgo(session.activated_at)}</p>
        </div>
        <div>
          <p className="font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Guest</p>
          <p className="text-gray-700 font-mono">u{session.guest_user_id}</p>
        </div>
      </div>

      <Link
        href={`/admin/queue_monitor/${session.id}`}
        className="block w-full text-center bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition-colors"
      >
        Open Session →
      </Link>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Index({ sessions = [] }) {
  const [view, setView] = useState('list')
  const [selectedPoint, setSelectedPoint] = useState(null)
  const [fitAllTrigger, setFitAllTrigger] = useState(0)

  // Auto-refresh every 15s
  useEffect(() => {
    const interval = setInterval(() => {
      router.reload({ only: ['sessions'] })
    }, 15000)
    return () => clearInterval(interval)
  }, [])

  // Build map points — one per session with coords
  const mapPoints = sessions
    .filter(s => s.lat && s.lng)
    .map(s => ({
      id:      s.id,
      title:   s.offer_name,
      lat:     s.lat,
      lng:     s.lng,
      session: s,
    }))

  // Group co-located sessions (same lat/lng rounded to 4dp)
  const locationKey = (lat, lng) => `${lat.toFixed(4)},${lng.toFixed(4)}`
  const locationGroups = {}
  mapPoints.forEach(p => {
    const key = locationKey(p.lat, p.lng)
    if (!locationGroups[key]) locationGroups[key] = []
    locationGroups[key].push(p.session)
  })

  const selectedSessions = selectedPoint
    ? (locationGroups[locationKey(selectedPoint.lat, selectedPoint.lng)] || [selectedPoint.session])
    : []

  const withCoords  = sessions.filter(s => s.lat && s.lng).length
  const noCoords    = sessions.length - withCoords

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Active Queues</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {sessions.length} open session{sessions.length !== 1 ? 's' : ''}
              {noCoords > 0 && ` · ${noCoords} without coordinates`}
              {' · '}
              <Link href="/admin/queue_monitor" className="text-blue-500 hover:underline">
                Queue Monitor →
              </Link>
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              href="/"
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-lg text-sm"
            >
              ← Offers
            </Link>
            <button
              onClick={() => { setView(v => v === 'list' ? 'map' : 'list'); setSelectedPoint(null) }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg text-sm"
            >
              {view === 'list' ? '🗺 Queue Map' : '☰ Queue List'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">

        {sessions.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-4xl mb-3">🗂</p>
            <p className="text-xl font-semibold text-gray-700">No active queue sessions</p>
            <p className="text-gray-500 mt-1">Sessions appear here when guests accept offers via the mobile app.</p>
            <Link href="/" className="inline-block mt-4 text-sm text-blue-500 hover:underline">
              Browse Offers →
            </Link>
          </div>
        ) : view === 'list' ? (

          // ── List view ──────────────────────────────────────────────────────
          <div className="space-y-4">
            {/* Mini map — scoped to marker spread */}
            {mapPoints.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
                <GoogleMap
                  points={mapPoints}
                  fitBounds={mapPoints.length > 1}
                  infoWindowEnabled={mapPoints.length <= 1}
                  className="h-52 w-full rounded-lg"
                />
              </div>
            )}
            {sessions.map(s => (
              <SessionCard key={s.id} session={s} />
            ))}
          </div>

        ) : (

          // ── Map view ───────────────────────────────────────────────────────
          <div className="space-y-4">
            {/* Map controls */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {mapPoints.length} of {sessions.length} sessions plotted
                {selectedPoint ? ` · ${selectedSessions.length} at selected location` : ''}
              </p>
              <button
                onClick={() => { setFitAllTrigger(n => n + 1); setSelectedPoint(null) }}
                className="bg-white border border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-1.5 px-3 rounded-lg text-sm shadow-sm"
              >
                🌐 Fit All
              </button>
            </div>

            {/* Map */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
              <GoogleMap
                points={mapPoints}
                fitBounds={mapPoints.length > 1}
                fitAllTrigger={fitAllTrigger}
                onMarkerClick={(point) => setSelectedPoint(point)}
                className="h-96 w-full rounded-lg"
              />
            </div>

            {/* Marker detail panel */}
            {selectedPoint ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-700">
                    📍 {selectedSessions.length > 1
                      ? `${selectedSessions.length} sessions at this location`
                      : selectedSessions[0]?.offer_name}
                  </p>
                  <button
                    onClick={() => setSelectedPoint(null)}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                  >
                    ✕ Dismiss
                  </button>
                </div>
                {/* Horizontal scroll for co-located sessions */}
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {selectedSessions.map(s => (
                    <SessionCard key={s.id} session={s} compact />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400 text-sm py-4">
                Click a marker to see session details
              </div>
            )}

            {/* Sessions without coordinates */}
            {noCoords > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-yellow-800 mb-2">
                  {noCoords} session{noCoords !== 1 ? 's' : ''} without offer coordinates (not shown on map)
                </p>
                <div className="flex gap-3 flex-wrap">
                  {sessions.filter(s => !s.lat || !s.lng).map(s => (
                    <Link
                      key={s.id}
                      href={`/admin/queue_monitor/${s.id}`}
                      className="text-xs bg-white border border-yellow-300 text-yellow-900 px-3 py-1.5 rounded-lg hover:bg-yellow-100"
                    >
                      #{s.id} {s.offer_name} →
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
